/**
 * Platform ve yetenek tespiti — Rapor 09 §11.6, Rapor 12 §7.
 *
 * ⚠️ **User-Agent'a göre karar VERİLMEZ; yeteneğe göre verilir.** UA dizesi
 *    taklit edilebilir ve tarayıcılar onu bilinçli olarak bulanıklaştırır.
 *    Yalnız iOS'un *bilinen platform kısıtları* için UA'ya bakılır — çünkü
 *    orada özellik tespiti mümkün değildir (API var, davranış farklı).
 *
 * ### İstemci destek matrisi (Rapor 09 §11.6)
 * | Platform | Durum | Bilinen farklar |
 * |----------|-------|-----------------|
 * | Chrome / Edge (masaüstü) | ✅ Birincil | Referans platform |
 * | Firefox (masaüstü) | 🟡 Destekli | Insertable Streams sınırlı → E2EE ve bazı efektler kapalı |
 * | Safari (macOS) | 🟡 Destekli | `getSynchronizationSources` doğrulanmalı (A11); ekran paylaşımı farkları |
 * | **Safari (iOS)** | 🟡 **Sınırlı** | **Autoplay politikası** · arka planda medya kesilir · Bluetooth cihaz değişimi · **ekran paylaşımı YOK** |
 * | Chrome (Android) | 🟡 Destekli | Arka plan kısıtları, pil optimizasyonu |
 * | IE-modu | ❌ Desteklenmez | Açıkça beyan edilir |
 */

export type FormFactor = "desktop" | "tablet" | "mobile";

export interface PlatformCapabilities {
  formFactor: FormFactor;
  /** Kaba işaretçi (dokunmatik) → daha büyük dokunma hedefleri gerekir. */
  coarsePointer: boolean;
  /** ⚠️ iOS Safari'de ekran paylaşımı **YOKTUR** — düğme gizlenir, gri değil. */
  canShareScreen: boolean;
  /**
   * ⚠️ iOS'ta **kullanıcı etkileşimi olmadan ses çalmaz**. Uzak ses için
   * "Sesi aç" düğmesi gösterilmelidir; sessizce başarısız olmak en kötü
   * kullanıcı deneyimidir ("kimseyi duymuyorum").
   */
  requiresAudioUnlockGesture: boolean;
  /** ⚠️ iOS'ta sekme arka plana alınınca medya KESİLİR (durdurulmaz, kesilir). */
  suspendsMediaInBackground: boolean;
  /** Insertable Streams — E2EE (v2) ve bazı efektler için gerekli. */
  hasInsertableStreams: boolean;
  /** `prefers-reduced-motion` — WCAG 2.2 (Rapor 09 §11.8). */
  prefersReducedMotion: boolean;
  isIos: boolean;
  isSafari: boolean;
}

/** Kırılım noktaları — `ui/tokens.css` ile AYNI değerler olmalıdır. */
export const BREAKPOINT = Object.freeze({ tablet: 768, desktop: 1024 } as const);

function detectFormFactor(width: number, coarse: boolean): FormFactor {
  if (width >= BREAKPOINT.desktop && !coarse) return "desktop";
  if (width >= BREAKPOINT.tablet) return "tablet";
  return "mobile";
}

export function detectPlatform(): PlatformCapabilities {
  const ua = navigator.userAgent;

  // ⚠️ iPadOS 13+ masaüstü UA bildirir; `maxTouchPoints` ile ayırt edilir.
  const isIos =
    /iPad|iPhone|iPod/.test(ua) || (ua.includes("Macintosh") && navigator.maxTouchPoints > 1);
  const isSafari = /^((?!chrome|android|crios|fxios).)*safari/i.test(ua);

  const coarsePointer = window.matchMedia("(pointer: coarse)").matches;
  const width = window.innerWidth;

  return {
    formFactor: detectFormFactor(width, coarsePointer),
    coarsePointer,
    // API'nin varlığı yetmez: iOS'ta `getDisplayMedia` tanımlı olabilir ama çalışmaz.
    canShareScreen: typeof navigator.mediaDevices?.getDisplayMedia === "function" && !isIos,
    requiresAudioUnlockGesture: isIos,
    suspendsMediaInBackground: isIos,
    hasInsertableStreams:
      "RTCRtpSender" in window &&
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- özellik tespiti; tip yok
      typeof (RTCRtpSender.prototype as any).createEncodedStreams === "function",
    prefersReducedMotion: window.matchMedia("(prefers-reduced-motion: reduce)").matches,
    isIos,
    isSafari,
  };
}

/**
 * Form faktörü değişimini izler (döndürme, pencere yeniden boyutlandırma).
 * Abonelik yöneticisi ızgara boyutunu buna göre yeniden hesaplar.
 */
export function watchFormFactor(cb: (f: FormFactor) => void): () => void {
  const mqTablet = window.matchMedia(`(min-width: ${BREAKPOINT.tablet}px)`);
  const mqDesktop = window.matchMedia(`(min-width: ${BREAKPOINT.desktop}px)`);
  const mqCoarse = window.matchMedia("(pointer: coarse)");

  const handler = (): void => cb(detectPlatform().formFactor);
  mqTablet.addEventListener("change", handler);
  mqDesktop.addEventListener("change", handler);
  mqCoarse.addEventListener("change", handler);

  return () => {
    mqTablet.removeEventListener("change", handler);
    mqDesktop.removeEventListener("change", handler);
    mqCoarse.removeEventListener("change", handler);
  };
}

/**
 * ⭐ **iOS ses kilidi.** Bir `<audio>`/`<video>` öğesini kullanıcı hareketi
 * içinde `play()` etmek, o sekmedeki sonraki otomatik oynatmaları serbest
 * bırakır. Bu fonksiyon "Sesi aç" düğmesinin `click` işleyicisinden çağrılır.
 *
 * ⚠️ Sessizce denemek YETMEZ: `play()` reddedilirse kullanıcı hiçbir şey
 *    duymaz ve nedenini anlamaz. Reddedilme UI'da **görünür** olmalıdır.
 */
export async function unlockAudioPlayback(el: HTMLMediaElement): Promise<boolean> {
  try {
    el.muted = false;
    await el.play();
    return true;
  } catch {
    return false;
  }
}
